/* =========================================================================
   CRWNZ homepage interactions — vanilla, no external libraries.
   Single namespace: window.CRWNZHome. Every feature is isolated in try/catch
   so one failure never blocks the others, and the page is fully usable with
   JS disabled. Honours prefers-reduced-motion and disables pointer effects on
   touch devices. Re-initialises on Shopify theme-editor section events.
   ========================================================================= */
(function () {
  'use strict';

  if (window.CRWNZHome && window.CRWNZHome._init) { return; } // never double-load

  var RM = window.matchMedia('(prefers-reduced-motion: reduce)');
  function reduced() { return RM.matches; }
  function finePointer() {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }
  // A reliable "has a real pointer" check. We deliberately do NOT hard-require
  // matchMedia('(pointer:fine)') here because Shopify's theme-editor preview runs
  // inside an iframe where that query is unreliable — so the cursor could wrongly
  // stay off in the customizer. Instead we gate on width + reduced-motion and let
  // the pointer events themselves filter touch (pointerType === 'touch' is ignored
  // in onMove/onDown). On the LIVE storefront a desktop mouse always activates it;
  // phones/tablets never fire non-touch pointer moves, so they stay off.
  function isDesktopPointer() {
    var coarseOnly = window.matchMedia('(pointer: coarse)').matches &&
                     !window.matchMedia('(pointer: fine)').matches;
    return !coarseOnly && window.innerWidth >= 768;
  }
  function safe(label, fn) {
    try { fn(); } catch (e) { if (window.console && console.warn) console.warn('[CRWNZ] ' + label, e); }
  }

  /* ---------------- count-up stats ---------------- */
  function countSpec(el) {
    var raw = (el.getAttribute('data-count') || el.getAttribute('data-target') || '').replace(/,/g, '');
    var to = parseFloat(raw) || 0;
    var dpAttr = el.getAttribute('data-dp');
    var dp = (dpAttr !== null && dpAttr !== '') ? parseInt(dpAttr, 10)
           : (raw.indexOf('.') !== -1 ? (raw.split('.')[1] || '').length : 0);
    return {
      to: to, dp: Math.min(dp, 2),
      prefix: el.getAttribute('data-prefix') || '',
      suffix: el.getAttribute('data-suffix') || ''
    };
  }
  function fmtCount(v, s) {
    var n = v.toFixed(s.dp);
    if (s.dp === 0) n = Math.round(v).toLocaleString('en-US');
    return s.prefix + n + s.suffix;
  }
  function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }

  function animateCount(el) {
    if (el.getAttribute('data-counted') === '1') return;
    el.setAttribute('data-counted', '1');
    var gen = (parseInt(el.getAttribute('data-gen') || '0', 10) + 1);
    el.setAttribute('data-gen', String(gen));
    function current() { return el.getAttribute('data-gen') === String(gen); }
    var s = countSpec(el), dur = 950, start = null;
    function step(ts) {
      if (!current()) return;                 // a reset / newer roll superseded this one
      if (start === null) start = ts;
      var p = Math.min((ts - start) / dur, 1);
      el.textContent = fmtCount(s.to * easeOutCubic(p), s);
      if (p < 1) requestAnimationFrame(step);
      else el.textContent = fmtCount(s.to, s);
    }
    requestAnimationFrame(step);
    // safety net: if rAF is throttled (background tab / editor iframe), still finish
    setTimeout(function () {
      if (current() && el.getAttribute('data-counted') === '1') el.textContent = fmtCount(s.to, s);
    }, dur + 600);
  }
  // reset a counter so it re-rolls next time it enters view; bumping the gen
  // cancels any in-flight animation for this element.
  function resetCount(el) {
    el.setAttribute('data-counted', '0');
    el.setAttribute('data-gen', String((parseInt(el.getAttribute('data-gen') || '0', 10) + 1)));
    el.textContent = fmtCount(0, countSpec(el));
  }
  function completeCount(el) {
    el.setAttribute('data-counted', '1');
    var s = countSpec(el);
    el.textContent = fmtCount(s.to, s);
  }
  function inView(el, slack) {
    var r = el.getBoundingClientRect();
    var h = window.innerHeight || document.documentElement.clientHeight;
    return r.top < h * (slack || 0.9) && r.bottom > 0;
  }
  // animate any counters that are on screen and not yet done (IO-independent fallback)
  function checkCounters(root) {
    (root || document).querySelectorAll('[data-count]:not([data-counted="1"]),[data-target]:not([data-counted="1"])')
      .forEach(function (el) { if (inView(el)) animateCount(el); });
  }
  function setupCounters(root) {
    var all = root.querySelectorAll('[data-count],[data-target]');
    if (!all.length) return;
    var live = [];
    Array.prototype.forEach.call(all, function (el) {
      if (countSpec(el).to === 0) {
        // Honest "coming soon" placeholder: a 0 that shimmers + pulses, never a
        // fake number. data-counted=1 keeps the count-up logic from touching it.
        el.classList.add('is-ph');
        el.textContent = '0';
        el.setAttribute('data-counted', '1');
      } else {
        el.classList.remove('is-ph');
        live.push(el);
      }
    });
    if (!live.length) return;
    if (reduced() || !('IntersectionObserver' in window)) {
      live.forEach(completeCount);
      return;
    }
    live.forEach(function (el) { resetCount(el); }); // visible 0 baseline
    // Animate each real counter once. Constant re-rolling on scroll was causing jank.
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.intersectionRatio >= 0.35) { animateCount(e.target); io.unobserve(e.target); }
      });
    }, { threshold: [0, 0.35] });
    live.forEach(function (el) { io.observe(el); });
    requestAnimationFrame(function () { checkCounters(root); }); // above-the-fold
  }

  /* ---------------- reveal on scroll ---------------- */
  function splitWords(root) {
    root.querySelectorAll('.reveal-words').forEach(function (node) {
      if (node.getAttribute('data-split') === '1') return;
      node.setAttribute('data-split', '1');
      node.innerHTML = node.innerHTML.split(/(<br\s*\/?>)/i).map(function (seg) {
        if (/^<br/i.test(seg)) return seg;
        return seg.replace(/(\S+)(\s*)/g, function (m, w, sp) {
          return '<span class="w" style="display:inline-block">' + w + '</span>' + sp;
        });
      }).join('');
    });
  }
  function showReveal(el) {
    if (el.classList.contains('reveal-words')) {
      el.querySelectorAll('.w').forEach(function (w, i) {
        w.style.transitionDelay = (i * 55) + 'ms';
        w.style.opacity = '1'; w.style.transform = 'none';
      });
      return;
    }
    el.style.opacity = '1'; el.style.transform = 'none';
  }
  function setupReveal(root) {
    splitWords(root);
    var items = root.querySelectorAll('[data-reveal],[data-reveal-line],.reveal-words');
    if (reduced() || !('IntersectionObserver' in window)) {
      items.forEach(showReveal);
      root.querySelectorAll('.reveal-words .w').forEach(function (w) {
        w.style.opacity = '1'; w.style.transform = 'none';
      });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { showReveal(e.target); io.unobserve(e.target); }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    items.forEach(function (el) { io.observe(el); });
    requestAnimationFrame(function () {
      items.forEach(function (el) {
        if (inView(el, 0.95)) { showReveal(el); io.unobserve(el); }
      });
    });
  }

  /* ---------------- mobile menu ---------------- */
  function setupMenu(root) {
    var burger = root.querySelector('#burger');
    var menu = root.querySelector('#menu');
    if (!burger || !menu || burger.getAttribute('data-bound') === '1') return;
    burger.setAttribute('data-bound', '1');
    function close() {
      document.body.classList.remove('menu-open');
      burger.setAttribute('aria-expanded', 'false');
    }
    burger.addEventListener('click', function () {
      var open = document.body.classList.toggle('menu-open');
      burger.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
    menu.querySelectorAll('a').forEach(function (a) { a.addEventListener('click', close); });
  }

  /* ---------------- calm liquid buttons (CSS driven) ---------------- */
  function setupMagnetic(root) {
    // Previous magnetic pointer tracking used per-button pointermove + rAF loops.
    // It looked snappy/messy and added unnecessary work while scrolling.
    // Keep the premium liquid hover purely in CSS: smoother, calmer, cheaper.
    root.querySelectorAll('[data-magnetic]').forEach(function (el) {
      if (el.getAttribute('data-mag') === '1') return;
      el.setAttribute('data-mag', '1');
      el.classList.add('crwnz-liquid-hover');
    });
  }


  /* ---------------- clean two-digit stat scramble ---------------- */
  function setupDigitScramble(root) {
    var items = Array.prototype.slice.call(root.querySelectorAll('.story-stats [data-crwnz-scramble]'));
    if (!items.length) return;

    var active = true;
    function randomDigits(len) {
      var out = '';
      for (var i = 0; i < len; i++) out += String(Math.floor(Math.random() * 10));
      return out;
    }

    items.forEach(function (el, idx) {
      if (el.getAttribute('data-scramble-ready') === '1') return;
      el.setAttribute('data-scramble-ready', '1');

      el.classList.add('n-scramble');

      // Keep this as one normal text value. No reels, columns, or split digits.
      el.textContent = '00';
      el.setAttribute('data-digits', '2');

      if (reduced()) return;

      var tick = function () {
        if (!active || reduced()) return;
        el.textContent = Math.random() < 0.22 ? '00' : randomDigits(2);
      };

      window.setTimeout(tick, idx ? 90 : 0);
      window.setInterval(tick, 140 + (idx * 24));
    });

    if ('IntersectionObserver' in window) {
      var host = items[0].closest('.story-stats') || items[0].closest('.story') || items[0];
      var io = new IntersectionObserver(function (entries) {
        active = !!(entries[0] && entries[0].isIntersecting);
      }, { threshold: 0.08 });
      io.observe(host);
    }
  }


  /* ---------------- sticky-nav glass, scroll cue fade, parallax, counters ---------------- */
  var scrollBound = false;
  function setupScroll() {
    if (scrollBound) return;
    scrollBound = true;
    var ticking = false;
    var parallaxEls = (window.innerWidth >= 1100 && !reduced() && !document.documentElement.classList.contains('crwnz-performance-mode')) ? Array.prototype.slice.call(document.querySelectorAll('[data-parallax-art]')).slice(0, 4) : [];
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        var y = window.pageYOffset || document.documentElement.scrollTop;
        var nav = document.getElementById('nav');
        if (nav) nav.classList.toggle('scrolled', y > 40);
        var cue = document.querySelector('.crwnz-home-section .scroll-cue');
        if (cue) {
          var o = Math.max(0, 1 - y / 220);
          cue.style.opacity = o.toFixed(3);
          cue.style.visibility = o <= 0.01 ? 'hidden' : 'visible';
        }
        if (parallaxEls.length && !document.documentElement.classList.contains('crwnz-performance-mode')) {
          parallaxEls.forEach(function (el) {
            var r = el.getBoundingClientRect();
            if (r.bottom > 0 && r.top < window.innerHeight) {
              var off = (r.top + r.height / 2 - window.innerHeight / 2) / window.innerHeight;
              el.style.transform = 'translateY(' + (off * -22) + 'px)';
            }
          });
        }
        ticking = false;
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---------------- nav spy: highlight the section you're viewing ---------------- */
  var navSpyBound = false;
  function setupNavSpy() {
    if (navSpyBound || !('IntersectionObserver' in window)) return;
    var nav = document.getElementById('nav');
    if (!nav) return;
    var links = nav.querySelectorAll('.nav-links a[href^="#"]:not(.nav-cta):not(.nav-shop)');
    if (!links.length) return;
    var map = {}, ids = [];
    Array.prototype.forEach.call(links, function (a) {
      var id = (a.getAttribute('href') || '').slice(1);
      if (id && document.getElementById(id)) { map[id] = a; ids.push(id); }
    });
    if (!ids.length) return;
    navSpyBound = true;
    function clearActive() { ids.forEach(function (id) { map[id].classList.remove('is-active'); }); }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting && map[e.target.id]) { clearActive(); map[e.target.id].classList.add('is-active'); }
      });
    }, { rootMargin: '-45% 0px -50% 0px', threshold: 0 });
    ids.forEach(function (id) { io.observe(document.getElementById(id)); });
  }

  /* ---------------- init ---------------- */
  function resolveRoot(scope) {
    var root = null;
    if (scope && scope.querySelector) {
      root = (scope.matches && scope.matches('[data-crwnz-homepage]'))
        ? scope : scope.querySelector('[data-crwnz-homepage]');
    }
    return root || document.querySelector('[data-crwnz-homepage]');
  }
  function init(scope) {
    var root = resolveRoot(scope);
    if (!root) return false;
    if (root.getAttribute('data-crwnz-init') === '1') return true;
    root.setAttribute('data-crwnz-init', '1');
    document.documentElement.classList.add('crwnz-motion-ready');
    safe('reveal', function () { setupReveal(root); });
    safe('counters', function () { setupCounters(root); });
    safe('digit scramble', function () { setupDigitScramble(root); });
    safe('menu', function () { setupMenu(root); });
    safe('scroll', function () { setupScroll(); });
    // Cursor spotlight removed by design — the animated void atmosphere
    // (crwnz-atmos.css) is the sole source of ambient movement/light.
    // Honour the section's "Background animation" toggle by freezing the blobs.
    if (root.getAttribute('data-bg-anim') === '0') {
      document.documentElement.classList.add('crwnz-bg-static');
    }
    safe('magnetic', function () { setupMagnetic(root); });
    safe('navspy', function () { setupNavSpy(); });
    return true;
  }

  // run on every lifecycle hook + a couple of timed retries (Shopify can defer/reorder)
  function boot() { safe('init', function () { init(document); }); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else { boot(); }
  window.addEventListener('load', boot);
  window.addEventListener('pageshow', boot);
  setTimeout(boot, 300);
  setTimeout(boot, 1200);
  // Shopify theme-editor lifecycle
  document.addEventListener('shopify:section:load', function (e) { safe('section:load', function () { init(e.target); }); });
  document.addEventListener('shopify:section:select', function (e) { safe('section:select', function () { init(e.target); }); });

  window.CRWNZHome = { _init: true, init: init, refreshCounters: function () { checkCounters(document); } };
})();
